import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import TourPageIcon from '@mui/icons-material/FindInPage';
import SendChatIcon from '@mui/icons-material/Send';
import { Accordion, Button } from '@mui/material';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import Skeleton from '@mui/material/Skeleton';
import Typography from '@mui/material/Typography';
import makeStyles from '@mui/styles/makeStyles';
import React, { useEffect, useState } from 'react';
// import { Search } from 'react-bootstrap-table2-toolkit';
import { useLocation } from 'react-router';
import {
  useRecoilStateLoadable,
  useRecoilValueLoadable,
  useSetRecoilState,
} from 'recoil';

import { agentUserId } from '../../recoil/atoms/agentUserId';
import { user } from '../../recoil/atoms/user';
import {
  methodNameEnum,
  refetchToggle,
} from '../../recoil/selectors/makePHPRequest';
import {
  endpointEnum,
  makeAPIRequest,
} from '../../services/rest/makeAPIRequest';
import { useIntercom } from '../hooks/useIntercom';
import { useDashboardTourContext } from '../pages/dashboardTour/DashboardTourContext';

// const { SearchBar } = Search;

const useStyles = makeStyles((theme) => ({
  helpSidebar: {
    background: '#fff',
    boxShadow: '0px 0px 28px 0px rgba(82, 63, 105, 0.08)',
    maxHeight: '100%',
    height: '100%',
    '.brand-dark &': {
      background: '#121c21',
      boxShadow: '0px 15px 50px 0px rgba(0, 0, 0, 0.2)',
    },
  },
  helpHead: {
    height: 178,
    '.brand-dark &': {
      color: '#fff !important',
    },
  },
  buttonRight: {
    border: '1px solid #0098C7',
    marginLeft: '1rem',
    marginRight: '0 !important',
  },
  helpSidebarContent: {
    flexDirection: 'column',
    display: 'flex',
    alignContent: 'flex-start',
  },
  helpSearch: {
    width: '267.5px',
    '.brand-dark &': {
      color: '#fff !important',
      background: 'transparent',
      borderColor: '#737373',
    },
  },
  helpBodyContainer: {
    overflow: 'hidden',
    overflowY: 'scroll',
    position: 'absolute',
    top: 178,
    bottom: 92,
    maskImage: 'linear-gradient(0deg, transparent 40px, red 150px)',
  },
  helpBody: {
    padding: '0 !important',
    paddingBottom: '50px !important',
    position: 'relative',
    maxHeight: '60vh',
    overflowY: 'scroll',
    '& img, iframe, p': {
      maxWidth: '100%',
      height: 'auto !important',
    },
    '& pre': {
      whiteSpace: 'pre-wrap',
    },
    '& img:hover': {
      cursor: 'pointer',
      opacity: 0.5,
    },
  },
  accordion: {
    boxShadow: 'none !important',
    '&.Mui-expanded:before': {
      opacity: 1,
    },
    '.brand-dark &': {
      background: '#121c21',
    },
  },
  articleTitle: {
    paddingLeft: 0,
    '& p': {
      fontSize: 15,
    },
  },
  articleBody: {
    padding: '0 5px',
    '& ul': {
      paddingInlineStart: 20,
    },
    '& h3': {
      fontSize: '1.15rem',
    },
    '.brand-dark &': {
      '& span': {
        color: '#fff !important',
      },
    },
  },
  helpFooter: {
    height: 92,
    background: '#fff',
    bottom: 0,
    right: 0,
    position: 'absolute',
    width: '100%',
    alignSelf: 'flex-end',
    '.brand-dark &': {
      background: '#121c21',
      color: '#a2a3b7',
      '& hr': {
        borderColor: '#666',
      },
    },
  },
  helpActManager: {
    background: '#edfaff',
    borderLeft: '4px solid #0098C7',
    padding: '1rem 2rem',
    '& p:first-child': {
      marginBottom: 2,
    },
    '& p:last-child': {
      marginBottom: 0,
    },
  },
  imageModal: {
    display: 'block',
    background: 'rgba(0, 0, 0, 0.25)',
    paddingTop: '5%',
  },
  modalDialog: {
    maxWidth: '50%',
    '& img': {
      maxWidth: '100%',
    },
  },
  modalHeader: {
    background: '#fff',
  },
}));

export const HELP_BAR_OPEN_CLASS = 'help-bar-open';

export function HelpSidebar() {
  const classes = useStyles();

  const [isSidebarOpen, setIsSidebarOpen] = React.useState(false);

  const [isImageModalOpen, setIsImageModalOpen] = React.useState('');

  const [imagePreviewSrc, setImagePreviewSrc] = React.useState('');

  const location = useLocation();

  const { openNewMessage, isBooted } = useIntercom();

  const hasHelpBarOpenClass = () =>
    document.body.classList.contains(HELP_BAR_OPEN_CLASS);

  useEffect(() => {
    setIsSidebarOpen(hasHelpBarOpenClass());

    const mutationObserver = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.attributeName === 'class') {
          const isSidebarOpen = hasHelpBarOpenClass();

          setIsSidebarOpen(isSidebarOpen);

          if (isSidebarOpen) {
            const pageTitleElement = document.getElementById('pageTitle');
            if (pageTitleElement) {
              setSearchQuery(pageTitleElement?.innerText ?? '');
            }
          }
        }
      });
    });

    mutationObserver.observe(document.body, {
      characterData: false,
      attributes: true,
      childList: false,
      subtree: false,
    });

    return () => {
      mutationObserver.disconnect();
    };
  }, []);

  const closeSidebar = () => {
    document.body.classList.remove(HELP_BAR_OPEN_CLASS);
  };

  const closeImageModal = () => {
    setIsImageModalOpen(false);
  };

  const [searchQuery, setSearchQuery] = useState('');

  const [displayArticles, setDisplayArticles] = useState([]);

  const { setState } = useDashboardTourContext();

  const showTour = () => {
    setState({ run: true, tourActive: true, stepIndex: 0 });
    closeSidebar();
  };

  const getHelpArticles = {
    call: methodNameEnum.GET_HELP_DESK_ARTICLES,
    pathname: window.location.pathname,
  };

  const helpArticles = useRecoilValueLoadable(
    makeAPIRequest({
      endpoint: endpointEnum.GET_HELP_DESK_ARTICLES,
      payloadJsonString: JSON.stringify(getHelpArticles),
      waitForPayload: !isSidebarOpen || searchQuery,
    })
  );

  const setRefetchToggleForArticles = useSetRecoilState(
    refetchToggle(endpointEnum.GET_HELP_DESK_ARTICLES)
  );

  const searchHelpArticles = useRecoilValueLoadable(
    makeAPIRequest({
      endpoint: endpointEnum.SEARCH_HELP_DESK_ARTICLES,
      payloadJsonString: JSON.stringify({
        call: methodNameEnum.SEARCH_HELP_DESK_ARTICLES,
        search: searchQuery,
        businessid: 995,
      }),
      waitForPayload: !isSidebarOpen || !searchQuery,
    })
  );

  const setRefetchToggleForSearchArticles = useSetRecoilState(
    refetchToggle(endpointEnum.SEARCH_HELP_DESK_ARTICLES)
  );

  useEffect(() => {
    setRefetchToggleForArticles({});
    setRefetchToggleForSearchArticles({});
  }, [searchQuery]);

  const bodyTransform = (articleBody) => {
    const helpBody = document.createElement('html');
    helpBody.innerHTML = articleBody;
    const helpLinks = Array.isArray(helpBody.getElementsByTagName('a'))
      ? helpBody.getElementsByTagName('a')
      : null;
    helpLinks?.forEach((link) => {
      link.setAttribute('target', '_blank');
    });
    const helpImgs = Array.isArray(helpBody.getElementsByTagName('img'))
      ? helpBody.getElementsByTagName('img')
      : null;
    helpImgs?.forEach((image) => {
      image.setAttribute('class', 'img--modal');
    });
    const newHtmlStr = '<html>' + helpBody.innerHTML + '</html>';
    return newHtmlStr;
  };

  const articleImg = document.getElementsByClassName('img--modal');

  (Array.isArray(articleImg) ? articleImg : null)?.forEach((image) => {
    image?.addEventListener('click', function () {
      setImagePreviewSrc(image.getAttribute('src'));
      setIsImageModalOpen(true);
    });
  });

  useEffect(() => {
    const pageTitleElement = document.getElementById('pageTitle');
    setSearchQuery(pageTitleElement?.innerText ?? '');

    let pageTitleObserver;

    const mutationObserver = new MutationObserver(() => {
      if (pageTitleObserver) return;

      if (!pageTitleElement) return;

      pageTitleObserver = new MutationObserver(() => {
        setSearchQuery(pageTitleElement?.innerText ?? '');
      });

      pageTitleObserver.observe(pageTitleElement, {
        characterData: true,
        attributes: false,
        childList: false,
        subtree: true,
      });
    });

    mutationObserver.observe(document, {
      characterData: false,
      attributes: false,
      childList: true,
      subtree: true,
    });

    return () => {
      mutationObserver.disconnect();

      if (pageTitleObserver) {
        pageTitleObserver.disconnect();
      }
    };
  }, []);

  useEffect(() => {
    const articles =
      searchQuery !== '' && searchHelpArticles?.state === 'hasValue'
        ? searchHelpArticles?.contents?.response?.data ?? []
        : searchQuery === '' && helpArticles?.state === 'hasValue'
        ? helpArticles?.contents?.response?.data ?? []
        : [];

    const filteredArticles = (Array.isArray(articles) ? articles : [])
      .filter((article) => article.title)
      .filter((article) => article.body);

    setDisplayArticles(filteredArticles);
  }, [helpArticles, searchHelpArticles]);

  const [expanded, setExpanded] = React.useState(false);

  const handleChange = (panel) => (_event, isExpanded) => {
    setExpanded(isExpanded ? panel : false);
  };

  const userAtom = useRecoilValueLoadable(user);
  const loginUser = userAtom.contents?.user ?? {};

  const [agentUserIdAtom] = useRecoilStateLoadable(agentUserId);
  const hasPageTour =
    window.location.pathname === '/manage/agents' ||
    window.location.pathname === `/my-profile/${loginUser.userid}/edit` ||
    window.location.pathname ===
      `/manage/agents/${agentUserIdAtom.contents.id}/edit` ||
    window.location.pathname === `/manage/numbers/unblocked` ||
    window.location.pathname === '/manage/powerlists' ||
    window.location.pathname === '/manage/queues' ||
    window.location.pathname === '/manage/cadences';
  useEffect(() => {
    const { pathname, search } = location;
    const shouldOpenChat =
      pathname.includes('my-profile') &&
      pathname.includes('edit') &&
      search.includes('chat=open') &&
      loginUser.userid &&
      isBooted;
    if (shouldOpenChat) {
      document.body.classList.add(HELP_BAR_OPEN_CLASS);
      setIsSidebarOpen(true);
      openNewMessage();
    }
  }, [isBooted, loginUser.userid, openNewMessage, location]);

  if (!isSidebarOpen) return null;

  return (
    <>
      {/* begin::Aside */}
      <div
        id="help_aside"
        className={`aside-right aside ${classes.helpSidebar} flex-column flex-row-auto`}
      >
        {/* begin::Aside Menu */}
        <div
          id="help_aside_menu_wrapper"
          className="aside-menu-wrapper flex-column-fluid d-block pt-1 helpArticle"
        >
          <div
            className="d-flex flex-row mt-3 pr-3 w-100"
            style={{ justifyContent: 'end' }}
          >
            <button
              onClick={() => closeSidebar()}
              className="btn btn-xs btn-icon btn-light btn-hover-primary"
              id="kt_quick_user_close"
            >
              <i className="ki ki-close icon-xs text-muted" />
            </button>
          </div>
          <div className={`h-100 px-5 ${classes.helpSidebarContent}`}>
            <div className={classes.helpHead}>
              <h1 className={`mw-100 ${classes.helpHeader}`}>Help Center</h1>
              <div className="mb-5 d-flex flex-row buttons">
                {hasPageTour ? (
                  <>
                    <Button
                      className={`flex-grow-1 mr-2 ${classes.buttonLeft}`}
                      variant="contained"
                      color="primary"
                      onClick={showTour}
                      disableElevation
                      data-pup="tour-page"
                    >
                      <TourPageIcon />
                      <span className="px-2">Tour page</span>
                    </Button>
                    <Button
                      id="intercomBtn"
                      className={`flex-grow-1 m-0 ${classes.buttonRight}`}
                      variant="contained"
                      color="secondary"
                      onClick={openNewMessage}
                      disableElevation
                      data-pup="send-chat"
                    >
                      <SendChatIcon className="pr-1" />
                      <span className="px-2">Send chat</span>
                    </Button>
                  </>
                ) : (
                  <Button
                    className={`flex-grow-1 m-0 ${classes.buttonRight}`}
                    variant="contained"
                    color="secondary"
                    onClick={openNewMessage}
                    disableElevation
                    data-pup="send-chat"
                  >
                    <SendChatIcon className="pr-1" />
                    <span className="px-2">Chat with support</span>
                  </Button>
                )}
              </div>
              {/* <div className="d-flex flex-row" id="searchContainer">
                react-bootstrap-table2-toolkit
                <SearchBar
                className={`flex-grow-1 ${classes.helpSearch}`}
                searchText={searchQuery}
                onSearch={(query) => setSearchQuery(query)}
              />
              </div> */}
              <h4 className="mt-3">Relevant Article(s)</h4>
              <hr style={{ marginBottom: 0, borderColor: '#a7a7a7' }} />
            </div>
            <div
              id="helpBody"
              className={`pt-5 mb-20 ${classes.helpBody}`}
              data-menu-vertical="1"
            >
              {(helpArticles?.state === 'loading' ||
                searchHelpArticles?.state === 'loading') && (
                <div>
                  <Skeleton
                    variant="rectangular"
                    style={{ marginBottom: 5, marginTop: 20 }}
                  />
                  <Skeleton variant="rectangular" style={{ marginBottom: 5 }} />
                  <Skeleton
                    variant="rectangular"
                    style={{ marginBottom: 15, width: '50%' }}
                  />
                  <Skeleton variant="rectangular" style={{ marginBottom: 5 }} />
                  <Skeleton
                    variant="rectangular"
                    style={{ marginBottom: 15, width: '50%' }}
                  />
                  <Skeleton variant="rectangular" style={{ marginBottom: 5 }} />
                  <Skeleton
                    variant="rectangular"
                    style={{ marginBottom: 15, width: '50%' }}
                  />
                  <Skeleton
                    variant="rectangular"
                    style={{ marginBottom: 5, marginTop: 20 }}
                  />
                  <Skeleton variant="rectangular" style={{ marginBottom: 5 }} />
                  <Skeleton
                    variant="rectangular"
                    style={{ marginBottom: 15, width: '50%' }}
                  />
                  <Skeleton variant="rectangular" style={{ marginBottom: 5 }} />
                  <Skeleton
                    variant="rectangular"
                    style={{ marginBottom: 15, width: '50%' }}
                  />
                  <Skeleton variant="rectangular" style={{ marginBottom: 5 }} />
                  <Skeleton
                    variant="rectangular"
                    style={{ marginBottom: 15, width: '50%' }}
                  />
                </div>
              )}
              {helpArticles.state !== 'loading' &&
                searchHelpArticles.state !== 'loading' &&
                displayArticles.map((article, i) => (
                  <Accordion
                    key={article?.id ?? i}
                    className={classes.accordion}
                    expanded={expanded === 'panel' + i}
                    onChange={handleChange('panel' + i)}
                  >
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon />}
                      aria-controls="panel1a-content"
                      id="panel1a-header"
                      className={classes.articleTitle}
                    >
                      <Typography>{article.title}</Typography>
                      <i className="menu-arrow" />
                    </AccordionSummary>
                    <AccordionDetails className={classes.articleBody}>
                      <Typography
                        dangerouslySetInnerHTML={{
                          __html: bodyTransform(article.body),
                        }}
                      ></Typography>
                    </AccordionDetails>
                  </Accordion>
                ))}
            </div>
            <div className={classes.helpFooter}>
              <p className="text-center mt-3 mb-5">
                <strong>Office Hours:</strong> 7AM to 5PM Mon-Fri PST{' '}
              </p>
              <hr />
              <p className="text-center fs-10">© 2023 Kixie</p>
            </div>
          </div>
        </div>
        {/* end::Aside Menu */}
      </div>
      {/* end::Aside */}
      {isImageModalOpen && (
        <div
          className={`modal fade ${classes.imageModal}`}
          tabIndex="-1"
          role="dialog"
          onClick={closeImageModal}
        >
          <div className={`modal-dialog ${classes.modalDialog}`}>
            <div
              className="modal-content"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                return false;
              }}
            >
              <div className={`modal-header ${classes.modalHeader}`}>
                <h4 className="modal-title">Preview</h4>
                <button
                  type="button"
                  className="close"
                  data-dismiss="modal"
                  onClick={closeImageModal}
                >
                  <span aria-hidden="true">&times;</span>
                  <span className="sr-only">Close</span>
                </button>
              </div>
              <div className="modal-body m-auto">
                <img src={imagePreviewSrc} alt="help-image-preview" />
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
